import { API } from "@/lib/api";
import {
  IdentificationCard, SpinnerGap, CaretLeft, CaretRight, PlayCircle, UserCircle, ArrowCounterClockwise,
} from "@phosphor-icons/react";

const CATEGORY_TABS = [
  { key: "our_team_needs_review", label: "Needs review" },
  { key: "our_team_matched", label: "Matched" },
  { key: "not_a_player", label: "Not a player" },
  { key: "opposing_team", label: "Opposing team" },
  { key: "all", label: "All" },
];

// AI Player Identification review status — a detected track is either
// dismissed (not a player / opposing team), matched (by AI or manually),
// or still needs review.
const TRACK_STATUS_STYLES = {
  ai: "bg-[#CCFF00]/15 text-[#CCFF00]",
  manual: "bg-blue-500/15 text-blue-400",
  opposing_team: "bg-orange-500/15 text-orange-400",
  not_a_player: "bg-zinc-500/15 text-zinc-400",
  review: "bg-amber-500/15 text-amber-400",
};
const TRACK_STATUS_LABELS = {
  ai: "AI matched",
  manual: "Manual",
  opposing_team: "Opposing team",
  not_a_player: "Not a player",
  review: "Needs review",
};
const trackReviewStatus = (category, matchSource) => {
  if (category === "not_a_player") return "not_a_player";
  if (category === "opposing_team") return "opposing_team";
  if (matchSource === "manual") return "manual";
  if (matchSource === "ai") return "ai";
  return "review";
};
const trackCategoryOf = (t) => t.category || (t.matched_player_id ? "our_team_matched" : "our_team_needs_review");

// The Player Identification (V4) review list — extracted verbatim (no
// behavior change) from GameDetail.jsx: track category tabs, bulk-dismiss
// bar, and per-track thumbnail/jersey/roster-assignment row.
export function PlayerIdentificationSection({
  tracks, tracksLoading, categoryFilter, setCategoryFilter, selectedTrackIds, setSelectedTrackIds,
  bulkActing, canManageGame, thumbIndexByTrack, setThumbIndexByTrack, currentTeamId, gameId,
  players, savingTrackId, onJumpToMoment, onUpdateTrackMatch, onDismissTrack, onRestoreTrack, onBulkDismiss,
}) {
  const toggleTrackSelected = (trackId) => {
    setSelectedTrackIds((prev) => {
      const next = new Set(prev);
      if (next.has(trackId)) next.delete(trackId); else next.add(trackId);
      return next;
    });
  };

  return (
    <div className="bg-[#121212] border border-zinc-800 p-6 rounded-sm lg:col-span-2" data-testid="player-id-section">
      <h2 className="font-heading text-2xl text-white flex items-center gap-2 mb-1"><IdentificationCard size={22} weight="bold" className="text-[#CCFF00]" /> Player identification</h2>
      <p className="text-xs text-zinc-500 max-w-xl mb-5">Jersey numbers are read automatically where the footage is clear enough and auto-matched only when unambiguous. Confirm or correct any track below, or dismiss tracks that aren't your players — the referee, sideline spectators, or the opposing team.</p>

      {tracksLoading && tracks.length === 0 ? (
        <p className="text-sm text-zinc-500 flex items-center gap-2" data-testid="player-id-loading"><SpinnerGap size={16} className="animate-spin" /> Loading tracks…</p>
      ) : tracks.length === 0 ? (
        <p className="text-sm text-zinc-500" data-testid="player-id-empty">No player tracks found for this analysis.</p>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="player-id-tabs">
            {CATEGORY_TABS.map((tab) => {
              const count = tab.key === "all" ? tracks.length : tracks.filter((t) => trackCategoryOf(t) === tab.key).length;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => { setCategoryFilter(tab.key); setSelectedTrackIds(new Set()); }}
                  data-testid={`player-id-tab-${tab.key}`}
                  className={
                    "font-label text-[11px] uppercase px-3 py-1.5 rounded-sm transition-colors duration-150 " +
                    (categoryFilter === tab.key ? "bg-[#CCFF00] text-black" : "bg-[#0A0A0A] border border-zinc-800 text-zinc-400 hover:text-white")
                  }
                >
                  {tab.label} ({count})
                </button>
              );
            })}
          </div>

          {(() => {
            const visibleTracks = categoryFilter === "all" ? tracks : tracks.filter((t) => trackCategoryOf(t) === categoryFilter);
            const allVisibleSelected = visibleTracks.length > 0 && visibleTracks.every((t) => selectedTrackIds.has(t.track_id));
            return (
              <>
                {canManageGame && visibleTracks.length > 0 && (
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={() => setSelectedTrackIds(allVisibleSelected ? new Set() : new Set(visibleTracks.map((t) => t.track_id)))}
                        data-testid="player-id-select-all-checkbox"
                        className="w-4 h-4 accent-[#CCFF00]"
                      />
                      Select all shown
                    </label>
                    {selectedTrackIds.size > 0 && (
                      <div className="flex items-center gap-2 flex-wrap" data-testid="player-id-bulk-bar">
                        <span className="text-xs text-zinc-500">{selectedTrackIds.size} selected</span>
                        <button type="button" onClick={() => onBulkDismiss("not_a_player")} disabled={bulkActing} data-testid="player-id-bulk-not-player-btn" className="font-label text-[11px] uppercase px-3 py-1.5 rounded-sm bg-zinc-700/40 text-zinc-300 hover:bg-zinc-700/70 transition-colors duration-150 disabled:opacity-50">
                          Mark not a player
                        </button>
                        <button type="button" onClick={() => onBulkDismiss("opposing_team")} disabled={bulkActing} data-testid="player-id-bulk-opposing-btn" className="font-label text-[11px] uppercase px-3 py-1.5 rounded-sm bg-orange-500/15 text-orange-400 hover:bg-orange-500/25 transition-colors duration-150 disabled:opacity-50">
                          Mark opposing team
                        </button>
                        <button type="button" onClick={() => setSelectedTrackIds(new Set())} data-testid="player-id-bulk-clear-btn" className="text-xs text-zinc-500 hover:text-white transition-colors duration-150">
                          Clear
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {visibleTracks.length === 0 ? (
                  <p className="text-sm text-zinc-500" data-testid="player-id-empty-filtered">No tracks in this category.</p>
                ) : (
                  <div className="space-y-2">
                    {visibleTracks.map((t) => {
                      const category = trackCategoryOf(t);
                      const isDismissed = category === "not_a_player" || category === "opposing_team";
                      const status = trackReviewStatus(category, t.match_source);
                      const thumbCount = t.thumbnail_count || 0;
                      const defaultIdx = thumbCount > 0 ? Math.floor((thumbCount - 1) / 2) : 0;
                      const shownIdx = thumbIndexByTrack[t.track_id] ?? defaultIdx;
                      const cycleThumb = (delta) => {
                        setThumbIndexByTrack((prev) => {
                          const cur = prev[t.track_id] ?? defaultIdx;
                          const next = (cur + delta + thumbCount) % thumbCount;
                          return { ...prev, [t.track_id]: next };
                        });
                      };
                      const shownTimestamp = t.thumbnail_timestamps?.[shownIdx];
                      return (
                        <div key={t.track_id} data-testid={`player-id-row-${t.track_id}`} className="flex items-center gap-3 flex-wrap bg-[#0A0A0A] border border-zinc-800 rounded-sm px-4 py-3">
                          {canManageGame && (
                            <input
                              type="checkbox"
                              checked={selectedTrackIds.has(t.track_id)}
                              onChange={() => toggleTrackSelected(t.track_id)}
                              data-testid={`player-id-checkbox-${t.track_id}`}
                              className="w-4 h-4 accent-[#CCFF00] shrink-0"
                            />
                          )}
                          {thumbCount > 0 ? (
                            <div className="relative shrink-0 w-20 h-24 rounded-sm overflow-hidden bg-black border border-zinc-800" data-testid={`player-id-thumb-${t.track_id}`}>
                              <img
                                key={shownIdx}
                                src={`${API}/teams/${currentTeamId}/games/${gameId}/tracks/${t.track_id}/thumbnail/${shownIdx}`}
                                alt={`Track ${t.track_id} preview`}
                                loading="lazy"
                                data-testid={`player-id-thumb-img-${t.track_id}`}
                                className="absolute inset-0 w-full h-full object-cover"
                              />
                              {thumbCount > 1 && (
                                <>
                                  <button type="button" onClick={() => cycleThumb(-1)} data-testid={`player-id-thumb-prev-${t.track_id}`} className="absolute left-0 top-0 bottom-0 w-6 flex items-center justify-center bg-gradient-to-r from-black/70 to-transparent text-white hover:text-[#CCFF00] transition-colors duration-150">
                                    <CaretLeft size={14} weight="bold" />
                                  </button>
                                  <button type="button" onClick={() => cycleThumb(1)} data-testid={`player-id-thumb-next-${t.track_id}`} className="absolute right-0 top-0 bottom-0 w-6 flex items-center justify-center bg-gradient-to-l from-black/70 to-transparent text-white hover:text-[#CCFF00] transition-colors duration-150">
                                    <CaretRight size={14} weight="bold" />
                                  </button>
                                  <span className="absolute bottom-1 right-1 font-label text-[9px] text-white/80 bg-black/50 rounded-sm px-1">{shownIdx + 1}/{thumbCount}</span>
                                </>
                              )}
                            </div>
                          ) : (
                            <div className="shrink-0 w-20 h-24 rounded-sm bg-[#151515] border border-zinc-800 flex flex-col items-center justify-center gap-1 text-zinc-600" data-testid={`player-id-thumb-empty-${t.track_id}`}>
                              <UserCircle size={22} weight="light" />
                              <span className="text-[9px] text-center leading-tight px-1">No preview</span>
                            </div>
                          )}
                          <div className="flex-1 min-w-[180px]">
                            <p className="text-sm text-white font-label">Track #{t.track_id}</p>
                            <p className="text-xs text-zinc-500" data-testid={`player-id-jersey-${t.track_id}`}>
                              {t.jersey_number_read ? `Jersey #${t.jersey_number_read} read` : "No jersey number read"}
                              {t.ocr_confidence != null && (
                                <span data-testid={`player-id-confidence-${t.track_id}`}> · {Math.round(t.ocr_confidence * 100)}% confidence</span>
                              )}
                              {" · "}{t.detection_count} detection{t.detection_count === 1 ? "" : "s"}
                            </p>
                            {shownTimestamp != null && (
                              <button
                                type="button"
                                onClick={() => onJumpToMoment(t.track_id, shownTimestamp)}
                                data-testid={`player-id-jump-${t.track_id}`}
                                className="mt-1 flex items-center gap-1 text-[#CCFF00] hover:text-[#B3E600] transition-colors duration-150 font-label text-xs"
                              >
                                <PlayCircle size={14} weight="fill" /> Jump to this moment
                              </button>
                            )}
                          </div>
                          <span
                            data-testid={`player-id-badge-${t.track_id}`}
                            className={
                              "font-label text-[10px] uppercase px-2 py-1 rounded-sm shrink-0 " + TRACK_STATUS_STYLES[status]
                            }
                          >
                            {TRACK_STATUS_LABELS[status]}
                          </span>
                          <select
                            value={t.matched_player_id || ""}
                            onChange={(e) => onUpdateTrackMatch(t.track_id, e.target.value)}
                            disabled={!canManageGame || isDismissed || savingTrackId === t.track_id}
                            data-testid={`player-id-select-${t.track_id}`}
                            className="bg-[#0A0A0A] border border-zinc-800 text-white text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-[#CCFF00] transition-colors duration-150 disabled:opacity-50 min-w-[190px]"
                          >
                            <option value="">Unassigned</option>
                            {players.filter((p) => p.active).map((p) => (
                              <option key={p.id} value={p.id}>#{p.jersey_number} {p.first_name} {p.last_name}</option>
                            ))}
                          </select>
                          {canManageGame && (
                            isDismissed ? (
                              <button
                                type="button"
                                onClick={() => onRestoreTrack(t.track_id)}
                                disabled={savingTrackId === t.track_id}
                                data-testid={`player-id-restore-${t.track_id}`}
                                className="flex items-center gap-1.5 font-label text-[11px] uppercase text-zinc-400 hover:text-[#CCFF00] transition-colors duration-150 disabled:opacity-50"
                              >
                                <ArrowCounterClockwise size={13} weight="bold" /> Move to review
                              </button>
                            ) : (
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => onDismissTrack(t.track_id, "not_a_player")}
                                  disabled={savingTrackId === t.track_id}
                                  data-testid={`player-id-dismiss-not-player-${t.track_id}`}
                                  className="font-label text-[10px] uppercase px-2 py-1.5 rounded-sm bg-zinc-800 text-zinc-400 hover:text-white transition-colors duration-150 disabled:opacity-50"
                                >
                                  Not a player
                                </button>
                                <button
                                  type="button"
                                  onClick={() => onDismissTrack(t.track_id, "opposing_team")}
                                  disabled={savingTrackId === t.track_id}
                                  data-testid={`player-id-dismiss-opposing-${t.track_id}`}
                                  className="font-label text-[10px] uppercase px-2 py-1.5 rounded-sm bg-zinc-800 text-orange-400 hover:text-orange-300 transition-colors duration-150 disabled:opacity-50"
                                >
                                  Opposing team
                                </button>
                              </div>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            );
          })()}
        </>
      )}
    </div>
  );
}
